import React, { useMemo } from "react";

import { UserX } from "lucide-react";

import { WidgetCard } from "./StatWidgets";

// Rejections by stage — "where does effort leak?". Dotted trend line
// (Power-BI-style): one point per status a candidate was rejected FROM,
// connected by a smooth dashed line, with count/share labeled above each
// point. The single worst stage gets a darker marker + bold red label.
//
// COLORS: text/line colors used to be set via inline `fill="var(--text-
// primary, #1e293b)"` etc. Inside an <svg>, that CSS custom property
// often doesn't inherit/resolve the way it does on regular HTML elements,
// so the literal fallback (#1e293b, a dark navy) was winning in BOTH
// light and dark mode — invisible against a dark card. Switched to the
// same Tailwind theme-aware utility classes already used successfully in
// the funnel widget (`fill-text-primary`, `fill-text-secondary`,
// `stroke-border-primary`) so colors correctly adapt with the app's
// light/dark theme instead of depending on an SVG-scoped CSS variable.
// The one true accent color (rose/red, for the trend line and the worst-
// stage highlight) stays an explicit hex since it's identical in both
// themes.

const VB_W = 560;
const VB_H = 230;
const PLOT_LEFT = 26;
const PLOT_RIGHT = 534;
const PLOT_TOP = 46;
const PLOT_BOTTOM = 150;

// Catmull-Rom → cubic-Bezier smoothing so the line curves through every
// point instead of joining them with hard angles.
const smoothPath = (pts) => {
    if (pts.length < 2) return "";
    let d = `M ${pts[0].x},${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        const c1x = p1.x + (p2.x - p0.x) / 6;
        const c1y = p1.y + (p2.y - p0.y) / 6;
        const c2x = p2.x - (p3.x - p1.x) / 6;
        const c2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`;
    }
    return d;
};

const RejectionsByStage = ({ rejections, loading }) => {
    const stages = rejections?.stages ?? [];
    const maxCount = useMemo(
        () => stages.reduce((m, s) => Math.max(m, s.count || 0), 0),
        [stages]
    );
    const topKey = rejections?.topRejectionStageKey ?? null;

    const points = useMemo(() => {
        if (!stages.length) return [];
        const n = stages.length;
        return stages.map((s, i) => {
            const x = n === 1 ? (PLOT_LEFT + PLOT_RIGHT) / 2 : PLOT_LEFT + ((PLOT_RIGHT - PLOT_LEFT) * i) / (n - 1);
            const ratio = maxCount > 0 ? (s.count || 0) / maxCount : 0;
            const y = PLOT_BOTTOM - ratio * (PLOT_BOTTOM - PLOT_TOP);
            return { ...s, x, y };
        });
    }, [stages, maxCount]);

    const linePath = useMemo(() => smoothPath(points), [points]);

    return (
        <WidgetCard
            icon={UserX}
            title="Rejections by Stage"
            insight={rejections?.insight}
            loading={loading}
            right={
                rejections?.totalRejections ? (
                    <span className="text-[11px] text-text-secondary">
                        {rejections.totalRejections} total
                    </span>
                ) : null
            }
        >
            {loading ? (
                <div className="h-[230px] w-full rounded-md bg-background-tertiary animate-pulse" />
            ) : stages.length === 0 ? (
                <p className="text-[12px] text-text-secondary py-8 text-center">
                    No rejections recorded yet.
                </p>
            ) : (
                <div className="w-full h-full flex-1">
                    <svg
                        viewBox={`0 0 ${VB_W} ${VB_H}`}
                        width="100%"
                        height="100%"
                        preserveAspectRatio="none"
                        style={{ display: "block" }}
                    >
                        {/* Baseline */}
                        <line
                            x1={PLOT_LEFT}
                            x2={PLOT_RIGHT}
                            y1={PLOT_BOTTOM}
                            y2={PLOT_BOTTOM}
                            className="stroke-border-primary"
                            strokeWidth={1}
                        />

                        {/* Dotted trend line */}
                        {points.length > 1 ? (
                            <path
                                d={linePath}
                                fill="none"
                                stroke="#fb7185"
                                strokeWidth={4}
                                strokeLinecap="round"
                                strokeDasharray="1,9"
                            />
                        ) : null}

                        {/* Points + labels */}
                        {points.map((p) => {
                            const isTop = topKey && p.key === topKey;
                            return (
                                <g key={p.key}>
                                    <circle
                                        cx={p.x}
                                        cy={p.y}
                                        r={isTop ? 7 : 5.5}
                                        fill={isTop ? "#e11d48" : "#831843"}
                                        className="stroke-background-primary"
                                        strokeWidth={2}
                                    />
                                    {/* Count + share, stacked above the point */}
                                    <text
                                        x={p.x}
                                        y={p.y - 20}
                                        textAnchor="middle"
                                        fontSize="13"
                                        fontWeight={700}
                                        fill={isTop ? "#e11d48" : undefined}
                                        className={isTop ? undefined : "fill-text-primary"}
                                    >
                                        {p.count}
                                    </text>
                                    {p.pct != null ? (
                                        <text
                                            x={p.x}
                                            y={p.y - 8}
                                            textAnchor="middle"
                                            fontSize="10"
                                            className="fill-text-secondary"
                                        >
                                            {Math.round(p.pct)}%
                                        </text>
                                    ) : null}
                                    {/* Category label below the axis */}
                                    <text
                                        x={p.x}
                                        y={PLOT_BOTTOM + 22}
                                        textAnchor="middle"
                                        fontSize="11"
                                        fontWeight={isTop ? 700 : 500}
                                        fill={isTop ? "#e11d48" : undefined}
                                        className={isTop ? undefined : "fill-text-primary"}
                                    >
                                        {p.label}
                                    </text>
                                </g>
                            );
                        })}
                    </svg>
                </div>
            )}
        </WidgetCard>
    );
};

export default RejectionsByStage;